/**
 * Turnifly Tab — platform challenges: Free (evergreen) + Pro (one-time purchase).
 * Spec: challenges .txt § 5.1.
 */

import { useState, useCallback, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Sparkles, Gem } from 'lucide-react'
import { BrowseChallengeCard } from './browse-challenge-card'
import { useBrowseChallenges } from '@turnifly/shared/hooks'
import { Skeleton } from '@turnifly/shared/ui'
import { EmptyState } from '@turnifly/shared/ui'

type Section = 'free' | 'pro'

export const TurniflyTab = memo(function TurniflyTab(): React.ReactElement {
  const { t } = useTranslation()
  const [section, setSection] = useState<Section>('free')
  const [page, setPage] = useState(0)

  const { data: freeChallenges, isLoading: loadingFree } = useBrowseChallenges({
    types: ['platform_free'],
    sort: 'popular',
    page: section === 'free' ? page : 0,
    enabled: section === 'free',
  })

  const { data: proChallenges, isLoading: loadingPro } = useBrowseChallenges({
    types: ['platform_pro'],
    sort: 'popular',
    page: section === 'pro' ? page : 0,
    enabled: section === 'pro',
  })

  const challenges = section === 'free' ? freeChallenges : proChallenges
  const isLoading = section === 'free' ? loadingFree : loadingPro

  const handleLoadMore = useCallback((): void => {
    setPage((p) => p + 1)
  }, [])

  return (
    <div className="space-y-4">
      {/* Section toggle */}
      <div className="flex gap-2">
        <SectionButton
          active={section === 'free'}
          icon={<Sparkles size={14} />}
          label={t('challenges.browse.freeChallenges')}
          onClick={() => { setSection('free'); setPage(0) }}
        />
        <SectionButton
          active={section === 'pro'}
          icon={<Gem size={14} />}
          label={t('challenges.browse.proChallenges')}
          onClick={() => { setSection('pro'); setPage(0) }}
        />
      </div>

      {/* Pro description */}
      {section === 'pro' && (
        <div className="rounded-xl bg-gradient-to-r from-primary/[0.08] to-accent/[0.08] border border-primary/20 px-4 py-3">
          <div className="flex items-center gap-2 mb-1">
            <Gem size={16} className="text-primary" />
            <span className="text-[13px] font-semibold text-text">Pro</span>
          </div>
          <p className="text-[11px] text-text-hint leading-relaxed">
            {t('challenges.browse.proDesc')}
          </p>
        </div>
      )}

      {/* Challenge list */}
      {isLoading && page === 0 ? (
        <div className="space-y-2.5">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[100px] rounded-2xl" />
          ))}
        </div>
      ) : (challenges?.length ?? 0) === 0 ? (
        <EmptyState
          icon={null}
          title={t('challenges.browse.noResults')}
          description={t('challenges.browse.noResultsDesc')}
        />
      ) : (
        <>
          <div className="space-y-2.5">
            {(challenges ?? []).map((c) => (
              <BrowseChallengeCard key={c.id} challenge={c} />
            ))}
          </div>

          {(challenges?.length ?? 0) >= 20 && (
            <button
              onClick={handleLoadMore}
              className="w-full mt-2 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] text-[13px] text-text-hint font-medium hover:bg-white/[0.05] transition-colors tap-highlight-transparent"
            >
              {t('challenges.browse.loadMore')}
            </button>
          )}
        </>
      )}
    </div>
  )
})

function SectionButton({ active, icon, label, onClick }: {
  active: boolean; icon: React.ReactNode; label: string; onClick: () => void
}): React.ReactElement {
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-xl text-[12px] font-semibold transition-colors tap-highlight-transparent ${
        active
          ? 'bg-primary/15 text-primary border border-primary/30'
          : 'bg-white/[0.03] text-text-hint border border-white/[0.06] hover:bg-white/[0.05]'
      }`}
    >
      {icon}
      {label}
    </button>
  )
}
